import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ComponentType, CSSProperties, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  Calendar,
  FolderPlus,
  GanttChart,
  LayoutGrid,
  LifeBuoy,
  List,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../../lib/auth/AuthProvider';
import { api } from '../../lib/api';
import { isCompanyAdmin } from '../../lib/team';
import { t } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { Button } from '../ui';
import { WhatsAppIcon } from '../WhatsAppIcon';

/**
 * Tour de onboarding (product tour) para la primera vez que un usuario llega a
 * /inicio. Coachmark propio (sin dependencias nuevas): overlay con "spotlight"
 * sobre elementos anclados con `data-tour="..."` + tooltip con la marca.
 *
 * - Persistencia por usuario en localStorage: `tb_tour_seen_{userId}`.
 * - Se auto-dispara una sola vez; se puede re-lanzar con `useTour().startTour()`.
 * - Si un elemento anclado no existe, el paso cae a un card centrado (fallback).
 * - Accesible: role="dialog", foco atrapado, Esc para cerrar.
 */

// --- Contexto (para re-lanzar el tour desde la topbar / home) ----------------

interface TourContextValue {
  /** Re-lanza el tour manualmente desde el paso 1. */
  startTour: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour debe usarse dentro de <TourProvider>');
  return ctx;
}

// --- Persistencia "ya lo vio" por usuario ------------------------------------

function tourSeenKey(userId: string): string {
  return `tb_tour_seen_${userId}`;
}

function hasSeenTour(userId: string): boolean {
  try {
    return localStorage.getItem(tourSeenKey(userId)) === '1';
  } catch {
    return false;
  }
}

function markTourSeen(userId: string): void {
  try {
    localStorage.setItem(tourSeenKey(userId), '1');
  } catch {
    /* almacenamiento no disponible: no bloquea el tour */
  }
}

// --- Definición de pasos -----------------------------------------------------

interface TourStep {
  id: string;
  /** Selector del elemento a resaltar. Si falta (o no existe) → card centrado. */
  target?: string;
  icon: LucideIcon;
  title: string;
  body: string;
  /** Ilustración opcional bajo el texto (pasos descriptivos). */
  illustration?: ReactNode;
}

/** Ícono compatible con lucide y con nuestro glyph de WhatsApp. */
type TourIcon = ComponentType<{ size?: number | string; className?: string }>;

/** Chip de ilustración: ícono + etiqueta (para pasos descriptivos). */
function IllustrationChip({ icon: Icon, label }: { icon: TourIcon; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-token border border-line bg-surface-2 px-2.5 py-1.5 text-xs font-medium text-ink">
      <Icon size={14} className="text-brand" />
      {label}
    </span>
  );
}

function buildSteps(): TourStep[] {
  return [
    {
      id: 'welcome',
      icon: Sparkles,
      title: t('tour.welcome.title'),
      body: t('tour.welcome.body'),
    },
    {
      id: 'projects',
      target: '[data-tour="projects"]',
      icon: FolderPlus,
      title: t('tour.projects.title'),
      body: t('tour.projects.body'),
    },
    {
      id: 'views',
      icon: LayoutGrid,
      title: t('tour.views.title'),
      body: t('tour.views.body'),
      illustration: (
        <div className="flex flex-wrap justify-center gap-2">
          <IllustrationChip icon={LayoutGrid} label={t('view.kanban')} />
          <IllustrationChip icon={List} label={t('view.list')} />
          <IllustrationChip icon={Calendar} label={t('view.calendar')} />
          <IllustrationChip icon={GanttChart} label={t('view.timeline')} />
        </div>
      ),
    },
    {
      id: 'team',
      target: '[data-tour="user-menu"]',
      icon: Users,
      title: t('tour.team.title'),
      body: t('tour.team.body'),
    },
    {
      id: 'notifications',
      target: '[data-tour="notifications"]',
      icon: Bell,
      title: t('tour.notifications.title'),
      body: t('tour.notifications.body'),
    },
    {
      id: 'help',
      icon: LifeBuoy,
      title: t('tour.help.title'),
      body: t('tour.help.body'),
      illustration: (
        <div className="flex flex-wrap justify-center gap-2">
          <IllustrationChip icon={LifeBuoy} label={t('support.title')} />
          <IllustrationChip icon={WhatsAppIcon} label={t('contact.whatsapp.title')} />
        </div>
      ),
    },
  ];
}

// --- Provider ----------------------------------------------------------------

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const steps = useMemo(buildSteps, []);
  const autoTriggered = useRef(false);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setRun(true);
  }, []);

  const closeTour = useCallback(() => {
    if (user) markTourSeen(user.id);
    setRun(false);
  }, [user]);

  // El onboarding de rubro tiene PRIORIDAD sobre el tour: si está pendiente, no
  // auto-disparamos el tour (si no, el tour le roba el foco al modal y el usuario
  // no llega a elegir su rubro). Query compartida con VerticalOnboarding (dedup).
  const { data: onboarding } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => api.get<{ vertical_pending: boolean }>('/onboarding/status'),
    enabled: !!user && isCompanyAdmin(user.role),
    staleTime: 60_000,
  });
  const onboardingPending = onboarding?.vertical_pending === true;

  // Auto-disparo la primera vez que el usuario llega a /inicio (una sola vez).
  useEffect(() => {
    if (!user || run || autoTriggered.current) return;
    if (location.pathname !== '/inicio') return;
    if (onboardingPending) return; // primero elegir rubro; el tour espera
    if (hasSeenTour(user.id)) return;
    autoTriggered.current = true;
    // Marcamos "visto" apenas se auto-dispara: garantiza que el tour aparezca
    // como máximo una vez por usuario/navegador, aunque el usuario recargue o
    // navegue sin cerrarlo explícitamente. La repetición manual (startTour desde
    // el menú) sigue disponible.
    markTourSeen(user.id);
    // Pequeño delay para asegurar que la topbar (anclas) ya montó.
    const id = window.setTimeout(() => {
      setStepIndex(0);
      setRun(true);
    }, 350);
    return () => window.clearTimeout(id);
  }, [user, run, location.pathname, onboardingPending]);

  const value = useMemo<TourContextValue>(() => ({ startTour }), [startTour]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {run && (
        <TourOverlay
          steps={steps}
          stepIndex={stepIndex}
          onStepChange={setStepIndex}
          onClose={closeTour}
        />
      )}
    </TourContext.Provider>
  );
}

// --- Overlay (spotlight + tooltip) -------------------------------------------

const TIP_WIDTH = 340;
const SPOT_PADDING = 6;

/** Atrapa el foco (Tab / Shift+Tab) dentro del card del tour. */
function trapFocus(e: KeyboardEvent, container: HTMLElement | null): void {
  if (!container) return;
  const focusables = container.querySelectorAll<HTMLElement>(
    'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
  );
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

interface TourOverlayProps {
  steps: TourStep[];
  stepIndex: number;
  onStepChange: (index: number) => void;
  onClose: () => void;
}

function TourOverlay({ steps, stepIndex, onStepChange, onClose }: TourOverlayProps) {
  const navigate = useNavigate();
  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const [rect, setRect] = useState<DOMRect | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);

  // Ubica el elemento objetivo y recalcula ante resize/scroll.
  useEffect(() => {
    const target = step.target;
    if (!target) {
      setRect(null);
      return;
    }
    const update = () => {
      const el = document.querySelector(target);
      if (el) {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        setRect(el.getBoundingClientRect());
      } else {
        setRect(null); // fallback: card centrado
      }
    };
    const raf = window.requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step.target, stepIndex]);

  // Foco al botón primario al cambiar de paso.
  useEffect(() => {
    primaryRef.current?.focus();
  }, [stepIndex]);

  // Esc para cerrar; Tab atrapado en el card.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Tab') {
        trapFocus(e, cardRef.current);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const goNext = () => {
    if (isLast) {
      onClose();
      navigate('/proyectos/nuevo');
    } else {
      onStepChange(stepIndex + 1);
    }
  };
  const goPrev = () => {
    if (!isFirst) onStepChange(stepIndex - 1);
  };

  // Posición del card: bajo el elemento anclado (las anclas viven en la topbar,
  // así que siempre hay lugar abajo); si no hay ancla, centrado.
  const tipWidth = Math.min(TIP_WIDTH, typeof window !== 'undefined' ? window.innerWidth - 24 : TIP_WIDTH);
  let cardStyle: CSSProperties;
  if (rect) {
    const vw = window.innerWidth;
    let left = rect.left + rect.width / 2 - tipWidth / 2;
    left = Math.max(12, Math.min(left, vw - tipWidth - 12));
    cardStyle = { position: 'fixed', top: rect.bottom + 12, left, width: tipWidth };
  } else {
    cardStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: tipWidth,
    };
  }

  const Icon = step.icon;
  const titleId = `tour-title-${step.id}`;
  const bodyId = `tour-body-${step.id}`;

  return (
    <div className="fixed inset-0 z-[60]" role="presentation">
      {/* Scrim: si hay ancla, 4 rectángulos que dejan un "hueco" sobre el
          objetivo (revela el elemento real); si no, scrim completo. */}
      {rect ? (
        <SpotlightScrim rect={rect} />
      ) : (
        <div aria-hidden className="fixed inset-0 bg-ink/50 backdrop-blur-[2px]" />
      )}

      {/* Aro de resaltado sobre el objetivo. */}
      {rect && (
        <div
          aria-hidden
          className="pointer-events-none fixed rounded-token ring-2 ring-brand ring-offset-2 ring-offset-bg transition-all"
          style={{
            top: rect.top - SPOT_PADDING,
            left: rect.left - SPOT_PADDING,
            width: rect.width + SPOT_PADDING * 2,
            height: rect.height + SPOT_PADDING * 2,
          }}
        />
      )}

      {/* Card / tooltip */}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        style={cardStyle}
        className="rounded-token border border-line bg-surface p-4 shadow-lg"
      >
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand/10 text-brand">
            <Icon size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="font-display text-base font-semibold text-ink">
              {step.title}
            </h2>
            <p id={bodyId} className="mt-1 text-sm text-muted">
              {step.body}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('tour.close')}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-token text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <X size={16} />
          </button>
        </div>

        {step.illustration && <div className="mt-3">{step.illustration}</div>}

        {/* Progreso */}
        <div className="mt-4 flex items-center gap-1.5" aria-hidden>
          {steps.map((s, i) => (
            <span
              key={s.id}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === stepIndex ? 'w-4 bg-brand' : 'w-1.5 bg-line',
              )}
            />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-token px-1 text-sm font-medium text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            {t('tour.skip')}
          </button>

          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button variant="secondary" onClick={goPrev} className="h-9 px-3 text-sm">
                <ArrowLeft size={15} />
                {t('tour.back')}
              </Button>
            )}
            <Button ref={primaryRef} onClick={goNext} className="h-9 px-3 text-sm">
              {isLast ? (
                <>
                  <FolderPlus size={15} />
                  {t('tour.cta')}
                </>
              ) : (
                <>
                  {t('tour.next')}
                  <ArrowRight size={15} />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Scrim con "hueco": 4 rectángulos sin superposición alrededor del objetivo. */
function SpotlightScrim({ rect }: { rect: DOMRect }) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const top = Math.max(0, rect.top - SPOT_PADDING);
  const bottom = Math.min(vh, rect.bottom + SPOT_PADDING);
  const left = Math.max(0, rect.left - SPOT_PADDING);
  const right = Math.min(vw, rect.right + SPOT_PADDING);
  const scrim = 'fixed bg-ink/50 backdrop-blur-[2px]';
  return (
    <div aria-hidden>
      <div className={scrim} style={{ top: 0, left: 0, width: vw, height: top }} />
      <div className={scrim} style={{ top: bottom, left: 0, width: vw, height: vh - bottom }} />
      <div className={scrim} style={{ top, left: 0, width: left, height: bottom - top }} />
      <div className={scrim} style={{ top, left: right, width: vw - right, height: bottom - top }} />
    </div>
  );
}
